// SPDX-License-Identifier: AGPL-3.0-or-later

import {CLOSE_DESCRIPTOR} from '@app/features/i18n/utils/CommonMessageDescriptors';
import {Button, ButtonVariant} from '@app/features/ui/button/Button';
import FocusRing from '@app/features/ui/focus_ring/FocusRing';
import MobileLayout from '@app/features/ui/state/MobileLayout';
import styles from '@app/features/updater/components/UpdatePrompt.module.css';
import UpdatePrompt from '@app/features/updater/state/UpdatePrompt';
import type {UpdatePromptKind} from '@app/features/updater/utils/UpdatePromptPolicy';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {ArrowClockwiseIcon, PhoneDisconnectIcon, XIcon} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';

const UPDATE_PROMPT_TITLE_DESCRIPTOR = msg({
	message: 'New update ready',
	comment: 'Title of the non-blocking prompt shown when a new app version is ready to apply.',
});
const UPDATE_PROMPT_RELOAD_BODY_DESCRIPTOR = msg({
	message: 'A new version is ready. Reload to get the latest fixes and features.',
	comment: 'Body of the update prompt when applying the update reloads the app.',
});
const UPDATE_PROMPT_RESTART_BODY_DESCRIPTOR = msg({
	message: 'The desktop update has been downloaded. Restart to install it.',
	comment: 'Body of the update prompt when a downloaded desktop update installs on restart.',
});
const UPDATE_PROMPT_CALL_WARNING_DESCRIPTOR = msg({
	message: 'You are in a call. Updating now will disconnect you.',
	comment: 'Short warning in the update prompt while the user is in a voice or video call.',
});
const UPDATE_PROMPT_UPDATE_NOW_DESCRIPTOR = msg({
	message: 'Update now',
	comment: 'Primary button in the update prompt that reloads or restarts to apply the update.',
});
const UPDATE_PROMPT_LATER_DESCRIPTOR = msg({
	message: 'Later',
	comment: 'Button in the update prompt that hides it for a few hours and reminds once more.',
});

function getUpdatePromptBodyDescriptor(kind: UpdatePromptKind) {
	return kind === 'native' ? UPDATE_PROMPT_RESTART_BODY_DESCRIPTOR : UPDATE_PROMPT_RELOAD_BODY_DESCRIPTOR;
}

export const UpdatePromptCard = observer(() => {
	const {i18n} = useLingui();
	const target = UpdatePrompt.target;
	if (!UpdatePrompt.visible || !target) return null;
	return (
		<section
			className={clsx(styles.card, MobileLayout.enabled && styles.cardMobile)}
			role="status"
			aria-live="polite"
			aria-labelledby="update-prompt-title"
			data-flx="updater.update-prompt.card"
		>
			<div className={styles.iconWrap} aria-hidden="true">
				<ArrowClockwiseIcon weight="bold" className={styles.icon} />
			</div>
			<div className={styles.body}>
				<h2 id="update-prompt-title" className={styles.title}>
					{i18n._(UPDATE_PROMPT_TITLE_DESCRIPTOR)}
				</h2>
				<p className={styles.text}>{i18n._(getUpdatePromptBodyDescriptor(target.kind))}</p>
				{UpdatePrompt.leavesCall && (
					<p className={styles.callWarning} data-flx="updater.update-prompt.call-warning">
						<PhoneDisconnectIcon weight="bold" className={styles.callWarningIcon} aria-hidden="true" />
						{i18n._(UPDATE_PROMPT_CALL_WARNING_DESCRIPTOR)}
					</p>
				)}
				<div className={styles.actions}>
					<Button
						small
						variant={ButtonVariant.PRIMARY}
						onClick={UpdatePrompt.applyNow}
						data-flx="updater.update-prompt.update-now"
					>
						{i18n._(UPDATE_PROMPT_UPDATE_NOW_DESCRIPTOR)}
					</Button>
					<Button
						small
						variant={ButtonVariant.SECONDARY}
						onClick={UpdatePrompt.snooze}
						data-flx="updater.update-prompt.later"
					>
						{i18n._(UPDATE_PROMPT_LATER_DESCRIPTOR)}
					</Button>
				</div>
			</div>
			<FocusRing offset={-2}>
				<button
					type="button"
					className={styles.close}
					aria-label={i18n._(CLOSE_DESCRIPTOR)}
					onClick={UpdatePrompt.dismiss}
					data-flx="updater.update-prompt.close"
				>
					<XIcon weight="bold" className={styles.closeIcon} />
				</button>
			</FocusRing>
		</section>
	);
});
